import React, { useEffect, useMemo, useState } from 'react'
import { useAuthFetchWithResponse } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useTypedSelector } from 'src/State/RootReducer'
import { INSTALLER_PARTNER } from 'src/Configuration'
import { EMobilityAccessRightsContext } from './EMobilityAccessRightsContext'
import accountLinksApi from '../../../Libs/installation-rights-management/api/account-link.api'
import { UserLevel } from 'src/State/User/UserModel'
import { useDebounce } from '@e3dc-react/shell/Hooks/useDebounce'

interface EMobilityAccessRightsProviderProps {
  children: React.ReactNode
  userLevel?: UserLevel
  userName?: string
  companyId?: number
}

/**
 * This provider gives the information on if a user has only read or also write rights for a given installation.
 *
 * This is especially relevant for users who are invited to an installation (user, installer, or partner).
 * Those users are linked to the installation via the accountLinks, which includes the information about
 * their access rights.
 *
 * For higher user levels, such as service, this linkage is irrelevant. Instead, those levels can generally access all installations with writing rights.
 *
 */
const EMobilityAccessRightsProvider: React.FC<EMobilityAccessRightsProviderProps> = ({ companyId, children, userLevel = 'USER', userName = '' }) => {
  const [hasEMobilityWritingRights, setHasEMobilityWritingRights] = useState<boolean>(false)
  const authFetch = useAuthFetchWithResponse()
  const activeInstallation = useTypedSelector(state => state.storage.activeInstallation)
  const accessRightsMustBeChecked = userLevel === 'USER' || INSTALLER_PARTNER.includes(userLevel)
  const refresh = useTypedSelector(state => state.apiView.refresh)
  const debouncedRefresh = useDebounce(refresh, 5000)

  useEffect(() => {
    if (!accessRightsMustBeChecked) {
      setHasEMobilityWritingRights(true)
    } else if (activeInstallation) {
      accountLinksApi
        .findAll(activeInstallation, authFetch)
        .then(res => {
          if (res.hasErrors) return console.error(res.error)
          if (res.value) {
            const currentUserAccountLink = res.value.find(
              accountLink => accountLink.userName === userName || accountLink.accountId.toString() === companyId?.toString()
            )
            setHasEMobilityWritingRights(currentUserAccountLink?.hasWriteAccess ?? false)
          }
        })
        .catch(err => console.error(err))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessRightsMustBeChecked, activeInstallation, companyId, userName, debouncedRefresh])

  const contextValue = useMemo(
    () => ({
      hasEMobilityWritingRights,
    }),
    [hasEMobilityWritingRights]
  )

  return <EMobilityAccessRightsContext.Provider value={contextValue}>{children}</EMobilityAccessRightsContext.Provider>
}

export default EMobilityAccessRightsProvider
